import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { API } from "@/lib/api";
import { toast } from "sonner";

const PHASE_DATA = {
  1: {
    title: "Klarhet",
    subtitle: "Att se",
    message: "Du har gått genom hela den första fasen. Du har vågat se dig själv — dina mönster, dina triggers och sambandet mellan tanke, känsla och beteende. Det kräver mod.",
    prompt: "Vad tar du med dig från fasen Klarhet? Vad har du sett om dig själv som du inte såg förut?",
    next: "2-1",
    nextPhase: "Frigörelse",
  },
  2: {
    title: "Frigörelse",
    subtitle: "Att släppa",
    message: "Du har avslutat den andra fasen. Du har mött det som gör ont, sett vad som inte är ditt att bära och börjat släppa. Det är inget litet arbete.",
    prompt: "Vad har du släppt — eller börjat släppa — under den här fasen? Hur känns det?",
    next: "3-1",
    nextPhase: "Förankring",
  },
  3: {
    title: "Förankring",
    subtitle: "Att vara",
    message: "Du har gått genom alla tre faserna. Du har sett, släppt och landat. Det som återstår är att ta med dig det här in i vardagen — inte som ett krav, utan som en påminnelse om vem du egentligen är.",
    prompt: "Vad vill du ta med dig från hela den här resan? Vad betyder emotionell frihet för dig nu?",
    next: null,
    nextPhase: null,
  },
};

export default function PhaseCompletePage() {
  const { phase } = useParams();
  const phaseNum = parseInt(phase);
  const data = PHASE_DATA[phaseNum];
  const navigate = useNavigate();
  const [reflection, setReflection] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (!data) { navigate("/dashboard"); return; }
    const t1 = setTimeout(() => setStep(1), 400);
    const t2 = setTimeout(() => setStep(2), 1600);
    const t3 = setTimeout(() => setStep(3), 2800);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [data, navigate]);

  useEffect(() => {
    if (data) {
      API.get(`/journey/phase-reflection/${phaseNum}`).then(res => {
        if (res.data.reflection) {
          setReflection(res.data.reflection);
          setSaved(true);
        }
      }).catch(() => {});
    }
  }, [phaseNum, data]);

  const handleSave = async () => {
    if (!reflection.trim()) return;
    setSaving(true);
    try {
      await API.post("/journey/phase-reflection", {
        phase: phaseNum,
        reflection: reflection.trim(),
      });
      setSaved(true);
      toast.success("Reflektion sparad");
    } catch {
      toast.error("Kunde inte spara");
    } finally {
      setSaving(false);
    }
  };

  const handleContinue = () => {
    if (data.next) {
      navigate(`/steg/${data.next}`);
    } else {
      navigate("/avslutning");
    }
  };

  if (!data) return null;

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      <div className="pt-24 sm:pt-28 pb-8 sm:pb-20 px-4 sm:px-6">
        <div className="max-w-xl mx-auto">

          {step >= 1 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-10"
            >
              <p className="text-sm uppercase tracking-[0.25em] text-[#A0655A] font-medium mb-4">
                Fas {phaseNum} avslutad
              </p>
              <h1 className="heading-font text-3xl sm:text-4xl font-medium text-[#111111] mb-2" data-testid="phase-complete-title">
                {data.title}
              </h1>
              <p className="text-[#111111]/40 text-sm">{data.subtitle}</p>
            </motion.div>
          )}

          {step >= 2 && (
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="mb-10"
            >
              <div className="bg-white border border-[#EBE3DF] rounded-2xl p-6 sm:p-8">
                <p className="text-[#111111]/70 leading-relaxed" data-testid="phase-complete-message">
                  {data.message}
                </p>
              </div>
            </motion.div>
          )}

          {step >= 3 && (
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="mb-8">
                <p className="text-sm font-medium text-[#111111] mb-3" data-testid="phase-reflection-prompt">
                  {data.prompt}
                </p>
                <Textarea
                  placeholder="Skriv fritt..."
                  value={reflection}
                  onChange={(e) => { setReflection(e.target.value); setSaved(false); }}
                  rows={5}
                  className="rounded-xl border-[#EBE3DF] focus:border-[#A0655A] resize-none text-base leading-relaxed mb-3"
                  data-testid="phase-reflection-input"
                />
                {!saved && reflection.trim() && (
                  <div className="flex justify-end">
                    <Button
                      onClick={handleSave}
                      disabled={saving}
                      variant="ghost"
                      size="sm"
                      className="text-[#A0655A] hover:text-[#8B5549] hover:bg-[#FBF5F2]"
                      data-testid="save-phase-reflection-btn"
                    >
                      {saving ? "Sparar..." : "Spara reflektion"}
                    </Button>
                  </div>
                )}
                {saved && (
                  <p className="text-xs text-[#A0655A]/60 text-right">Sparad</p>
                )}
              </div>

              <div className="text-center">
                <Button
                  onClick={handleContinue}
                  className="bg-[#A0655A] hover:bg-[#8B5549] text-white rounded-full px-8 py-6 text-base"
                  data-testid="continue-after-phase-btn"
                >
                  {data.next ? `Vidare till ${data.nextPhase}` : "Se min avslutning"}
                  <ChevronRight size={18} className="ml-2" />
                </Button>
              </div>
            </motion.div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
